import { useState, useEffect } from 'react';

interface TimeOfDayMood {
  name: string;
  overlay: string;
  message?: string;
}

const getTimeOfDayMood = (): TimeOfDayMood => {
  const hour = new Date().getHours();
  
  // Late night (11PM - 3AM) - darker, more mysterious
  if (hour >= 23 || hour < 3) {
    return {
      name: 'midnight',
      overlay: 'bg-gradient-to-b from-purple-950/20 via-transparent to-purple-950/10',
      message: 'The night watches with you.',
    };
  }
  
  // Deep night (3AM - 5AM) - eerie, liminal
  if (hour >= 3 && hour < 5) {
    return {
      name: 'witching',
      overlay: 'bg-gradient-to-b from-red-950/15 via-transparent to-blue-950/15',
      message: 'Few visit at this hour.',
    };
  }
  
  // Early morning (5AM - 7AM) - quiet awakening
  if (hour >= 5 && hour < 7) {
    return {
      name: 'dawn',
      overlay: 'bg-gradient-to-b from-amber-950/10 via-transparent to-transparent',
      message: 'A new day begins.',
    };
  }
  
  // Morning (7AM - 12PM) - neutral/clear
  if (hour >= 7 && hour < 12) {
    return {
      name: 'morning',
      overlay: 'bg-transparent',
    };
  }
  
  // Afternoon (12PM - 5PM) - slightly warm
  if (hour >= 12 && hour < 17) {
    return {
      name: 'afternoon',
      overlay: 'bg-gradient-to-b from-amber-950/5 via-transparent to-transparent',
    };
  }
  
  // Evening (5PM - 8PM) - golden hour feel
  if (hour >= 17 && hour < 20) {
    return {
      name: 'evening',
      overlay: 'bg-gradient-to-b from-orange-950/10 via-transparent to-transparent',
      message: 'The day fades.',
    };
  }
  
  // Night (8PM - 11PM) - getting darker
  return {
    name: 'night',
    overlay: 'bg-gradient-to-b from-blue-950/15 via-transparent to-purple-950/10',
    message: 'Night settles in.',
  };
};

const TimeMoodOverlay = () => {
  const [mood, setMood] = useState<TimeOfDayMood>(getTimeOfDayMood);
  const [showMessage, setShowMessage] = useState(false);

  useEffect(() => {
    // Update mood every minute
    const interval = setInterval(() => {
      setMood(getTimeOfDayMood());
    }, 60000);

    // Show message briefly on first visit of the day
    const lastMoodShown = localStorage.getItem('last_mood_shown');
    const today = new Date().toDateString();
    
    if (lastMoodShown !== today && mood.message) {
      setTimeout(() => {
        setShowMessage(true);
        localStorage.setItem('last_mood_shown', today);
        
        setTimeout(() => setShowMessage(false), 5000);
      }, 3000);
    }

    return () => clearInterval(interval);
  }, [mood.message]);

  return (
    <>
      {/* Mood overlay */}
      <div 
        className={`fixed inset-0 pointer-events-none z-30 transition-all duration-[3000ms] ${mood.overlay}`}
        style={{ mixBlendMode: 'multiply' }}
      />
      
      {/* Time-based message */}
      {showMessage && mood.message && (
        <div 
          className="fixed top-20 left-1/2 -translate-x-1/2 text-xs text-muted-foreground/40 animate-fade-in z-50 pointer-events-none"
          style={{ animationDuration: '2s' }}
        >
          {mood.message}
        </div>
      )}
    </>
  );
};

export default TimeMoodOverlay;
